import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import flow from 'lodash/flow';
import isEmpty from 'lodash/isEmpty';
import Swatch from '@gcc/pip-components/dist/Swatch';
import { Typography } from '@one-thd/sui-atomic-components';
import {
  useDataModel,
  extend,
  string,
  shape,
  params,
  arrayOf,
  bool
} from '@thd-nucleus/data-sources';
import { TConfiguratorChoice, TConfiguratorOption } from '../_propTypes';
import ConfiguratorTemplate from '../ConfiguratorTemplate';
import { SwatchOverride } from './styles';
import { onSelectedChoice } from '../../actions';
import { selectChoiceOptionById } from '../../selectors/selections';
import { selectParentSuperSkuId } from '../../selectors/configurator';
import { pricingNodeSelector } from '../../selectors/pricing';
import { getMediaUrlForChoice, choiceHasPriceForChoice, findColorAttribute } from './ColorSwatchesUtils';

const ColorSwatches = ({ choices, onChoiceSelected, option }) => {
  const dispatch = useDispatch();
  const [thumbnailImgs, setThumbnailImgs] = useState([]);
  const choiceSelection = useSelector(selectChoiceOptionById(option.id));
  const parentSku = useSelector(selectParentSuperSkuId);
  const { choicesWithPrices } = useSelector(pricingNodeSelector);

  const skip = thumbnailImgs && thumbnailImgs.length > 0;
  const { data: metadata } = useDataModel('metadata', {
    skip: !parentSku || skip,
    variables: { parentId: parentSku }
  });

  useEffect(() => {
    const metaDataAttributes = metadata?.metadata?.attributes;
    if (!metaDataAttributes || !metaDataAttributes.length) return;

    const colorAttribute = findColorAttribute(metaDataAttributes);
    const attributeValues = (colorAttribute && Array.isArray(colorAttribute.attributeValues))
      ? colorAttribute.attributeValues
      : [];
    setThumbnailImgs(attributeValues);
  }, [metadata]);

  const getMediaUrl = (choice) => getMediaUrlForChoice(thumbnailImgs, choice);
  const choiceHasPrice = (choice) => choiceHasPriceForChoice(choicesWithPrices, choice);

  return (
    <div className="option-wrapper">
      <Typography paragraph style={{ marginBottom: '4px' }}>
        {option.shortLabel}:&nbsp;<strong>{choiceSelection?.value}</strong>
      </Typography>
      <div style={{ display: 'flex', flexWrap: 'wrap' }}>
        {choices.map((choice) => (choiceHasPrice(choice) ? (
          <SwatchOverride key={`${choice.id}_${choice.internalName}`} selected={choice.id === choiceSelection.id}>
            <Swatch
              imageSrc={getMediaUrl(choice)}
              label={choice.shortLabel}
              onClick={() => dispatch(onSelectedChoice(choice))}
              selected={choice === choiceSelection}
            />
          </SwatchOverride>
        ) : null))}
      </div>
    </div>
  );
};

ColorSwatches.propTypes = {
  choices: PropTypes.arrayOf(TConfiguratorChoice).isRequired,
  onChoiceSelected: PropTypes.func.isRequired,
  option: TConfiguratorOption.isRequired,
};

ColorSwatches.dataModel = extend(
  {
    metadata: params({ parentId: string().isRequired() }).shape({
      attributes: arrayOf(shape({
        attributeName: string(),
        attributeValues: arrayOf(shape({
          value: string(),
          swatchGuid: string(),
        })),
        isSwatch: bool()
      })),
    })
  });

export default flow(ConfiguratorTemplate)(ColorSwatches);
